import {
  getClientLegalDocuments,
  type ClientLegalDocumentId,
} from '../constants/clientLegalContent';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import { ClientLegalDocumentBody } from './ClientLegalDocumentBody';
import styles from './ClientLegalDocumentSubsections.module.css';

export type ClientLegalOpenState = Record<ClientLegalDocumentId, boolean>;

export function createClosedClientLegalOpenState(): ClientLegalOpenState {
  return {
    termsOfUse: false,
    privacyPolicy: false,
    accessibilityStatement: false,
  };
}

const CLIENT_LEGAL_AGREE_LABEL_KEYS: Record<ClientLegalDocumentId, MessageKey> = {
  termsOfUse: 'platform.termsAgreeTermsOfUse',
  privacyPolicy: 'platform.termsAgreePrivacyPolicy',
  accessibilityStatement: 'platform.termsAgreeAccessibility',
};

type ClientLegalDocumentSubsectionsProps = {
  idPrefix: string;
  open: ClientLegalOpenState;
  theme?: 'dark' | 'light' | 'profile';
  disabled?: boolean;
  showGroupHeading?: boolean;
  showAgreeCheckboxes?: boolean;
  agreed?: ClientLegalOpenState;
  onToggleOpen: (documentId: ClientLegalDocumentId) => void;
  onAgreedChange?: (documentId: ClientLegalDocumentId, agreed: boolean) => void;
};

export function ClientLegalDocumentSubsections({
  idPrefix,
  open,
  theme = 'light',
  disabled = false,
  showGroupHeading = false,
  showAgreeCheckboxes = false,
  agreed,
  onToggleOpen,
  onAgreedChange,
}: ClientLegalDocumentSubsectionsProps) {
  const { language, t } = useI18n();
  const documents = getClientLegalDocuments(language);
  const isDark = theme === 'dark';
  const isProfile = theme === 'profile';

  return (
    <div className={styles.root}>
      {showGroupHeading ? (
        <h4
          className={isProfile ? styles.groupHeadingProfile : styles.groupHeading}
        >
          {t('profile.hub.termsClientHeading')}
        </h4>
      ) : null}
      {documents.map((document) => {
        const documentOpen = open[document.id];
        const sectionClass = [
          styles.section,
          isProfile
            ? styles.sectionProfile
            : isDark
              ? styles.sectionDark
              : styles.sectionLight,
        ].join(' ');

        return (
          <section
            key={document.id}
            className={sectionClass}
            aria-labelledby={`${idPrefix}-${document.id}-heading`}
          >
            <button
              type="button"
              id={`${idPrefix}-${document.id}-heading`}
              className={[
                styles.toggle,
                isProfile
                  ? styles.toggleProfile
                  : isDark
                    ? styles.toggleDark
                    : styles.toggleLight,
              ].join(' ')}
              aria-expanded={documentOpen}
              onClick={() => onToggleOpen(document.id)}
              disabled={disabled}
            >
              <svg
                className={[
                  styles.caret,
                  isProfile
                    ? styles.caretProfile
                    : isDark
                      ? styles.caretDark
                      : styles.caretLight,
                  documentOpen ? styles.caretOpen : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                viewBox="0 0 16 16"
                width={16}
                height={16}
                aria-hidden
              >
                <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.75" />
              </svg>
              <span>{document.heading}</span>
            </button>
            {documentOpen ? (
              <div
                className={[
                  styles.body,
                  isProfile
                    ? styles.bodyProfile
                    : isDark
                      ? styles.bodyDark
                      : styles.bodyLight,
                ].join(' ')}
                id={`${idPrefix}-${document.id}-body`}
              >
                <ClientLegalDocumentBody document={document} />
              </div>
            ) : null}
            {showAgreeCheckboxes ? (
              <label
                className={[
                  styles.agreeLabel,
                  isDark ? styles.agreeLabelDark : styles.agreeLabelLight,
                ].join(' ')}
              >
                <input
                  type="checkbox"
                  className={styles.agreeCheckbox}
                  checked={agreed?.[document.id] ?? false}
                  disabled={disabled}
                  onChange={(event) => onAgreedChange?.(document.id, event.target.checked)}
                />
                <span>{t(CLIENT_LEGAL_AGREE_LABEL_KEYS[document.id])}</span>
              </label>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
